import React from "react";
import { Content } from "_c/Modals";
import { useToggle } from "@/hooks/useToggle";
import SaloonMasterPickerModal from "_c/Modals/saloon-master-picker-modal";
import TasksHistory from "./TasksHistory";
import ExternalLinks from "./ExternalLinks";
import ClientInfo from "./ClientInfo";
import Header from "./Header";
import ChipsList from "_c/ChipsList";
import Notifications from "./Notifications";
import { useAvailableTabs, useOpenModalAnalytics, useTabState } from "./hooks";
import { analyticsAction } from "@/utils/analytics";
import Footer from "./Footer";
import { useTranslation } from "react-i18next";
import { MASTER_CLIENTS_NS } from "@/i18n/ns";
import type { tClientProfileViewerProps } from "./types";
import type { tBizClient } from "@/types";
import { useTaskEditor } from "@/hooks/useTaskEditor";
import { useClientEditor } from "@/hooks/useClientEditor";

type tProps = Omit<tClientProfileViewerProps, "client" | "isOpen"> & {
  client: tBizClient;
};

const ClientProfile = (props: tProps) => {
  const openTaskEditor = useTaskEditor();
  const openEditor_ = useClientEditor();
  const openEditor = () => {
    openEditor_({
      domain: props.client.domain,
      master: props.client.master,
      saloon: props.client.saloon,
      client: props.client,
    });
  };
  const { client } = props;
  const { t, ready } = useTranslation(["common", "errors", MASTER_CLIENTS_NS], {
    useSuspense: false,
  });
  useOpenModalAnalytics();
  const { tab, setTab } = useTabState(props.defaultTab || "stats");
  const tabsList = useAvailableTabs();
  const [isMasterPickerOpen, toggleMasterPicker] = useToggle();
  const onRemove = (client: tBizClient) => {
    if (props.onRemove) {
      props.onRemove(client);
    }
    props.onClose();
    analyticsAction({
      category: "clients",
      action: "card_delete_click",
    });
  };
  const onBookClick = () => {
    if (client.domain === "saloon") {
      toggleMasterPicker();
    } else {
      openTaskEditor({ client: client?._id });
      props.onClose();
    }
    analyticsAction({
      category: "clients",
      action: "card_book_click",
    });
  };
  if (!client || !ready) return null;
  return (
    <>
      <Header
        onBookClick={onBookClick}
        isBookingDisabled={props.disableActions}
        client={client}
        t={t}
        isActionsDisabled={props.disableActions}
        onRemove={onRemove}
        onEdit={openEditor}
      />
      <Content
        css={`
          color: var(--font-color-accent);
        `}
      >
        <div>
          <ExternalLinks client={client} />
          <div
            css={`
              margin-top: 10px;
            `}
          >
            <ChipsList active={tab} onChange={setTab} items={tabsList} t={t} />
            {tab === "info" && <ClientInfo client={client} t={t} />}
            {tab === "notify" && <Notifications client={client} t={t} />}
            {tab === "stats" && <TasksHistory client={client} t={t} />}
          </div>
        </div>
      </Content>
      <Footer onClose={props.onClose} />
      <SaloonMasterPickerModal
        onSelect={({ masterId }: { masterId: string }) => {
          openTaskEditor({
            client: client._id,
            masterId,
            saloonOnly: true,
          });
          props.onClose();
        }}
        isOpen={isMasterPickerOpen}
        onClose={toggleMasterPicker}
        saloonId={client?.saloon}
        t={t}
      />
    </>
  );
};

export default ClientProfile;
